import React from 'react';
import useResizeObserver, { type ResizeHandler } from 'use-resize-observer';
import { useDeepCompareMemo, useDeepCompareEffect } from 'use-deep-compare';
import { completeTierPropObject, type OptionalTiersObject } from 'utilsTS/react';
import { TIER } from 'utilsTS/dom';
import { ContainerQueryContext } from './ContainerQueryContext';
import { getContainerWidths } from './getContainerWidths';

type ResizeObserverParameters<E extends Element> = Parameters<typeof useResizeObserver<E>>;

export type ContainerQueryOptions<E extends Element> = ResizeObserverParameters<E>[0] & {
    onMatch?: (matchingTier: TIER, matchingTiers: TIER[]) => void;
};

export const useContainerQuery = <E extends Element>(
    widths?: Record<string, string>,
    widthPrefix = '',
    { onResize, onMatch, ...options }: ContainerQueryOptions<E> = {},
) => {
    const { defaultTier, tierWidths: defaultTierWidths } = React.useContext(ContainerQueryContext);
    const [width, setWidth] = React.useState<number>();
    const onMatchRef = React.useRef(onMatch);

    const handleResize = React.useCallback<ResizeHandler>(
        (size) => {
            setWidth(size.width);
            onResize?.(size);
        },
        [onResize],
    );
    const { ref } = useResizeObserver<E>({ ...options, onResize: handleResize });

    const tierWidths = useDeepCompareMemo(
        () => (widths ? getContainerWidths(widths, widthPrefix) : defaultTierWidths),
        [widths, widthPrefix, defaultTierWidths],
    );

    const isMatchingTier = React.useCallback(
        (tier: TIER) => {
            if (!tier) return false;
            if (!tierWidths[tier] && tier !== TIER.XS) return false;
            if (!width) return tierWidths[defaultTier] >= tierWidths[tier];
            return width >= tierWidths[tier];
        },
        [width, defaultTier, tierWidths],
    );

    const matchingTier = Object.values(TIER).reverse().find(isMatchingTier) || defaultTier;
    const matchingTiers = Object.values(TIER).filter(isMatchingTier);

    const getTierProp = React.useCallback(
        <T>(data: OptionalTiersObject<T> | T): T => completeTierPropObject(data)[matchingTier] as T,
        [matchingTier],
    );

    useDeepCompareEffect(() => {
        onMatchRef.current?.(matchingTier, matchingTiers);
    }, [matchingTier, matchingTiers]);

    return {
        ref,
        width,
        isMatchingTier,
        getTierProp,
        matchingTier,
        matchingTiers,
        tierWidths,
    };
};
